import { get, set } from 'lodash';

import type { selectors, types } from '../../ducks/buildDetailsFilterBar';
import { BuildDetailsDiffFragFragment } from '../../graphql/__generated__/graphql';
import { DetailsGroupBy } from '../../ducks/buildDetailsFilterBar/constants';
import { titleCase } from '../../global/utils';
import { BuildDetailsGroupArray } from './types';

export const dedupe = (item: types.FilterItem, index: number, array: types.FilterItem[]) => {
  const { key } = item;
  const foundItem = array.findIndex((nestedItem) => nestedItem.key === key);
  return foundItem === index;
};

/**
 * Get the group by filter value for a diff.
 * @param diff
 * @param prop
 */
export const getGroupBy = (
  diff: BuildDetailsDiffFragFragment,
  prop: DetailsGroupBy,
): string|null|undefined => {
  switch (prop) {
    case DetailsGroupBy.Device:
      return diff.snapshot?.device;
    case DetailsGroupBy.OS:
      return diff.snapshot?.operatingSystem ? titleCase(diff.snapshot.operatingSystem) : null;
    case DetailsGroupBy.TestName:
      return diff.snapshot?.testName;
    case DetailsGroupBy.SuiteName:
      return diff.snapshot?.suiteName;
    case DetailsGroupBy.Browser:
      return diff.snapshot?.browser ? titleCase(diff.snapshot.browser) : null;
    case DetailsGroupBy.StorybookGroup:
      return diff.snapshot?.name.split('/')[0];
    case DetailsGroupBy.StorybookName:
      return diff.snapshot?.name.split('/')[1];
    default:
      return null;
  }
};

export const sortGroupByName = <T extends [string, any], >(group1: T, group2: T) => (
  group1[0].localeCompare(group2[0])
);

/**
 * Recursively parse an object to extract all IDs from it in order
 * @param obj
 */
export const getIdsFromGroup = (obj: BuildDetailsGroupArray): string[] => {
  const values = Object
    .entries(obj)
    .sort(sortGroupByName)
    .map((item) => item[1]);

  const firstVal = values.find((value) => value);
  if (firstVal && Array.isArray(firstVal)) {
    return (
      values as unknown as BuildDetailsDiffFragFragment[][]
    ).reduce((prev: string[], curr) => (
      prev.concat(...curr.map((diff) => diff.id))
    ), []);
  }

  return values
    .reduce((prev: string[], curr: BuildDetailsGroupArray) => (
      prev.concat(...getIdsFromGroup(curr))
    ), []);
};

const sortByName = (item1: BuildDetailsDiffFragFragment, item2: BuildDetailsDiffFragFragment) => (
  item1.snapshot && item2.snapshot ? item1.snapshot?.name.localeCompare(item2.snapshot?.name) : 1
);

export const buildGroupObject = (
  diffs: BuildDetailsDiffFragFragment[],
  filterValues: ReturnType<typeof selectors.buildDetailsFilterValues>,
): BuildDetailsGroupArray => {
  const groupObj: BuildDetailsGroupArray = {};
  const sorted = [...diffs].sort(sortByName);

  sorted.forEach((diff) => {
    if (
      (
        filterValues.os.length > 0 &&
        filterValues.os.findIndex((osKey) => {
          const [os = false, osVersion = false] = osKey.split('|');
          return (
            diff.snapshot?.operatingSystem === os &&
            (diff.snapshot?.operatingSystemVersion ?? '') === osVersion
          );
        }) === -1
      ) ||
      (
        filterValues.device.length > 0 &&
        !filterValues.device.includes(diff.snapshot?.device as string)
      ) ||
      (
        filterValues.browser.length > 0 &&
        !filterValues.browser.includes(diff.snapshot?.browser as string)
      ) ||
      (
        filterValues.search && !(
          diff.snapshot?.name.toLowerCase().includes(filterValues.search.toLowerCase()) ||
          diff.snapshot?.testName?.toLowerCase().includes(filterValues.search.toLowerCase()) ||
          diff.snapshot?.suiteName?.toLowerCase().includes(filterValues.search.toLowerCase())
        )
      ) ||
      (
        filterValues.status.length > 0 &&
        !filterValues.status.includes(diff.status)
      )
    ) {
      return;
    }

    const filterGroups = filterValues.groupBy
      .map((groupBy) => getGroupBy(diff, groupBy) ?? 'Unknown');
    const pathGroup = filterGroups.length > 0 ? filterGroups : ['All Snapshots'];

    const index = get(groupObj, pathGroup, []).length;
    set(groupObj, pathGroup.concat(index), diff);
  });

  return groupObj;
};
